/**
 * One conversation, one thread.
 *
 * The rail listed a separate chat for almost every question. The cause was that
 * "which thread does this answer belong to" was read from React state inside a
 * mutation: state updates are asynchronous, so a second question recorded
 * before the component re-rendered saw no thread id, decided it was starting a
 * conversation, and created one. Two entries in the rail, one line of enquiry,
 * and the second entry holding the follow-up that only makes sense after the
 * first.
 *
 * The window is small in the happy path and wide exactly when it matters. An
 * answer takes twenty to thirty seconds and a refusal comes back immediately,
 * so the case that opens the race is a refused answer followed by the person
 * rephrasing straight away — which is the moment they are most likely to be
 * asking the same thing twice.
 *
 * Two rules, and neither is expressible in component state:
 *
 *   1. The current thread is held in a mutable cell, written the instant the
 *      server answers rather than at the next render.
 *   2. Creation is serialised. A turn arriving while a thread is being opened
 *      waits for that thread and appends to it, instead of racing it with a
 *      second one.
 *
 * Kept out of the component so it can be tested. This app has no DOM harness,
 * and a race that only reproduces under real timing is precisely the thing that
 * needs a test with timing it controls.
 */

export interface ThreadTurn {
  question: string;
  answer: string | null;
  citations: string[];
  model: string | null;
}

export interface ThreadPort {
  startThread: (turn: ThreadTurn) => Promise<{ threadId: string; turnId: string }>;
  addTurn: (threadId: string, turn: ThreadTurn) => Promise<{ turnId: string }>;
}

export interface ThreadWriter {
  /** Records a turn, opening a thread only if this conversation has none. */
  record: (turn: ThreadTurn) => Promise<{ threadId: string; turnId: string }>;
  /** Abandons the current conversation. The next turn opens a new thread. */
  reset: () => void;
  /** Continues an existing thread, as when one is reopened from the rail. */
  resume: (threadId: string) => void;
  /** The thread being written to, or null before the first turn lands. */
  current: () => string | null;
}

export function createThreadWriter(port: ThreadPort): ThreadWriter {
  let threadId: string | null = null;
  let opening: Promise<{ threadId: string; turnId: string }> | null = null;

  const record = async (turn: ThreadTurn): Promise<{ threadId: string; turnId: string }> => {
    if (threadId !== null) {
      const added = await port.addTurn(threadId, turn);
      return { threadId, turnId: added.turnId };
    }

    if (opening !== null) {
      /*
       * Somebody else is already opening the thread this turn belongs to. Wait
       * for it and append, rather than starting a second conversation about
       * the same question.
       */
      const started = await opening;
      const added = await port.addTurn(started.threadId, turn);
      return { threadId: started.threadId, turnId: added.turnId };
    }

    // Assigned before the await so a turn arriving during the round trip finds
    // it. This is the whole fix; a check-then-act with an await between is what
    // produced the duplicates.
    const pending = port.startThread(turn);
    opening = pending;
    try {
      const started = await pending;
      threadId = started.threadId;
      return started;
    } finally {
      /*
       * Cleared whether it resolved or threw. Left set after a failure, every
       * later turn would await a rejected promise and the conversation could
       * never be recorded again; and a failed open must be retryable, because
       * the answer is already on the reader's screen.
       */
      opening = null;
    }
  };

  return {
    record,
    reset: () => {
      threadId = null;
      opening = null;
    },
    resume: (id: string) => {
      threadId = id;
      opening = null;
    },
    current: () => threadId,
  };
}
